/**
 * Theme bridge: cart drawer triggers, cart count sync, fixed header offset.
 * Works with custom headers without per-file cart wiring if aria-label="Cart" is used.
 */
(function () {
  const CART_DRAWER_SELECTOR = 'cart-drawer';
  const HEADER_GROUP_ID = 'shopify-section-group-header-group';

  function getCartDrawer() {
    return document.querySelector(CART_DRAWER_SELECTOR);
  }

  function openCartDrawer(trigger) {
    const drawer = getCartDrawer();
    if (drawer && typeof drawer.open === 'function') {
      drawer.open(trigger);
      return;
    }
    window.location.href = window.routes?.cart_url || '/cart';
  }

  function findCartTriggers() {
    const group = document.getElementById(HEADER_GROUP_ID);
    const scope = group || document;
    const selectors = [
      '[data-pi-cart-trigger]',
      '[aria-label="Cart"]',
      '[aria-label*="cart" i]',
    ];
    const nodes = new Set();
    selectors.forEach((sel) => {
      scope.querySelectorAll(sel).forEach((el) => nodes.add(el));
    });
    return [...nodes];
  }

  function bindCartTriggers() {
    const drawer = getCartDrawer();
    findCartTriggers().forEach((el) => {
      if (el.dataset.piCartBound === 'true') return;
      el.dataset.piCartBound = 'true';

      if (el.tagName === 'A') {
        el.setAttribute('role', 'button');
        if (drawer) el.setAttribute('href', '#');
      }

      el.addEventListener('click', (event) => {
        if (!getCartDrawer()) return;
        event.preventDefault();
        openCartDrawer(el);
      });

      el.addEventListener('keydown', (event) => {
        if (!getCartDrawer()) return;
        if (event.code !== 'Space' && event.code !== 'Enter') return;
        event.preventDefault();
        openCartDrawer(el);
      });
    });
  }

  function updateCartCounts(count) {
    const n = typeof count === 'number' ? count : null;
    const apply = (itemCount) => {
      document.querySelectorAll('[data-pi-cart-count]').forEach((el) => {
        el.textContent = String(itemCount);
        if (itemCount > 0) {
          el.removeAttribute('hidden');
          el.style.display = '';
        } else {
          el.setAttribute('hidden', 'hidden');
        }
      });

      document.querySelectorAll(
        '#shopify-section-group-header-group .cart-badge, #shopify-section-group-header-group .hd-cart-badge, #shopify-section-group-header-group .header__cart-badge, #shopify-section-group-header-group .pet-header__cart-badge, #shopify-section-group-header-group [class*="cart-badge"]',
      ).forEach((el) => {
        if (el.hasAttribute('data-pi-cart-count')) return;
        el.textContent = String(itemCount);
        el.hidden = itemCount === 0;
      });
    };

    if (n !== null) {
      apply(n);
      return;
    }

    fetch(`${window.routes?.cart_url || '/cart'}.js`)
      .then((r) => r.json())
      .then((cart) => apply(cart.item_count || 0))
      .catch(() => {});
  }

  function findAnnouncementBar(group) {
    return (
      group?.querySelector('[data-pi-announcement]') ||
      group?.querySelector('.announcement-bar, [class*="announcement-bar"], [id^="announcement-bar-"]')
    );
  }

  function usesStackedHeaderGroup(group) {
    if (!group) return false;
    const style = getComputedStyle(group);
    return style.position === 'sticky' || style.position === 'fixed';
  }

  function findSiteHeader(group) {
    return (
      group?.querySelector('[data-pi-site-header]') ||
      group?.querySelector('.site-header, .pet-header, .header, [id^="header-"]')
    );
  }

  function setFixedHeaderOffset() {
    const group = document.getElementById(HEADER_GROUP_ID);
    if (!group) return;

    const announcement = findAnnouncementBar(group);
    const header = findSiteHeader(group);

    const announcementHeight = announcement
      ? Math.max(announcement.getBoundingClientRect().height, 44)
      : 0;

    document.documentElement.style.setProperty(
      '--pi-announcement-height',
      `${announcementHeight}px`,
    );

    const stackedGroup = usesStackedHeaderGroup(group);

    if (stackedGroup) {
      if (header) header.style.top = '';
      document.body.style.paddingTop = '';
      const groupHeight = group.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--pi-header-height', `${groupHeight}px`);
      return;
    }

    const headerPosition = header ? getComputedStyle(header).position : '';
    if (header && (headerPosition === 'fixed' || headerPosition === 'sticky')) {
      header.style.top = `${announcementHeight}px`;
    }

    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    const paddingTop = announcementHeight + headerHeight;
    const outOfFlow = headerPosition === 'fixed' || headerPosition === 'absolute';

    document.documentElement.style.setProperty('--pi-header-height', `${paddingTop}px`);
    document.body.style.paddingTop =
      outOfFlow && paddingTop > 0 ? `${paddingTop}px` : '';
  }

  function init() {
    bindCartTriggers();
    updateCartCounts();
    setFixedHeaderOffset();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const count = event?.cartData?.item_count;
        updateCartCounts(typeof count === 'number' ? count : undefined);
        bindCartTriggers();
      });
    }

    window.addEventListener('resize', setFixedHeaderOffset);
    window.addEventListener('scroll', setFixedHeaderOffset, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
